/**
 * Natural Language Query Interface Utilities
 */

import { QueryResult, ExtendedArchiMateDiagram } from './extended-types';
import { ArchiMateElement, ArchiMateRelationship } from './types';

export function parseQuery(query: string): {
  action: string;
  subject: string;
  filters: Record<string, string>;
} {
  const lowerQuery = query.toLowerCase();

  // Determine action
  let action = 'find';
  if (lowerQuery.includes('show') || lowerQuery.includes('display')) action = 'show';
  if (lowerQuery.includes('list')) action = 'list';
  if (lowerQuery.includes('what')) action = 'what';
  if (lowerQuery.includes('who')) action = 'who';
  if (lowerQuery.includes('where')) action = 'where';
  if (lowerQuery.includes('depend')) action = 'dependencies';

  // Extract subject
  let subject = '';
  const elementTypes = [
    'service',
    'component',
    'application',
    'database',
    'server',
    'process',
    'function',
  ];
  for (const type of elementTypes) {
    if (lowerQuery.includes(type)) {
      subject = type;
      break;
    }
  }

  // Extract filters
  const filters: Record<string, string> = {};

  // Layer filters
  const layers = ['business', 'application', 'technology', 'strategy', 'physical', 'motivation'];
  for (const layer of layers) {
    if (lowerQuery.includes(layer)) {
      filters.layer = layer;
    }
  }

  // Security filters
  if (lowerQuery.includes('pii') || lowerQuery.includes('personal')) {
    filters.dataClassification = 'pii';
  }
  if (lowerQuery.includes('public')) {
    filters.zone = 'public';
  }

  // Ownership filters
  const teamMatch = query.match(/team:\s*"([^"]+)"|team:\s*(\S+)/i);
  if (teamMatch) {
    filters.team = teamMatch[1] || teamMatch[2];
  }

  return { action, subject, filters };
}

export function executeQuery(
  query: string,
  diagram: ExtendedArchiMateDiagram
): QueryResult {
  const { action, subject, filters } = parseQuery(query);

  let elements: ArchiMateElement[] = [];
  let relationships: ArchiMateRelationship[] = [];
  let explanation = '';

  // Execute based on action
  switch (action) {
    case 'dependencies':
      return findDependencies(query, diagram);

    case 'who':
      return findOwnership(query, diagram);

    case 'show':
    case 'list':
    case 'find':
    default:
      elements = findElements(query, diagram, filters);
      explanation = `Found ${elements.length} element(s) matching "${query}"`;
      break;
  }

  // Get related relationships
  const elementIds = new Set(elements.map((e) => e.id));
  relationships = diagram.relationships.filter(
    (rel) => elementIds.has(rel.sourceId) || elementIds.has(rel.targetId)
  );

  return {
    query,
    elements,
    relationships,
    explanation,
    suggestions: generateSuggestions(query, elements),
  };
}

function findElements(
  query: string,
  diagram: ExtendedArchiMateDiagram,
  filters: Record<string, string>
): ArchiMateElement[] {
  const lowerQuery = query.toLowerCase();

  return diagram.elements.filter((element) => {
    // Text matching
    const nameMatch = element.name.toLowerCase().includes(lowerQuery);
    const typeMatch = element.type.toLowerCase().includes(lowerQuery);
    const docMatch = element.documentation?.toLowerCase().includes(lowerQuery);

    if (!nameMatch && !typeMatch && !docMatch) {
      // Check for keyword matches
      const keywords = extractKeywords(query);
      const keywordMatch = keywords.some(
        (keyword) =>
          element.name.toLowerCase().includes(keyword) ||
          element.type.includes(keyword) ||
          element.documentation?.toLowerCase().includes(keyword)
      );

      if (!keywordMatch) return false;
    }

    // Apply filters
    if (filters.layer && element.layer !== filters.layer) return false;

    if (filters.dataClassification) {
      const security = diagram.elementMetadata[element.id]?.security;
      if (security?.dataClassification !== filters.dataClassification) return false;
    }

    if (filters.zone) {
      const security = diagram.elementMetadata[element.id]?.security;
      if (security?.zone !== filters.zone) return false;
    }

    if (filters.team) {
      const ownership = diagram.elementMetadata[element.id]?.ownership;
      if (ownership?.team !== filters.team) return false;
    }

    return true;
  });
}

function findDependencies(
  query: string,
  diagram: ExtendedArchiMateDiagram
): QueryResult {
  // Extract element name from query like "what depends on X"
  const match = query.match(/depends?\s+on\s+(.+?)(\?|$)/i);
  if (!match) {
    return {
      query,
      elements: [],
      relationships: [],
      explanation: 'Could not parse dependency query. Try: "What depends on [element name]?"',
    };
  }

  const targetName = match[1].trim();
  const targetElement = diagram.elements.find(
    (e) => e.name.toLowerCase() === targetName.toLowerCase()
  );

  if (!targetElement) {
    return {
      query,
      elements: [],
      relationships: [],
      explanation: `Element "${targetName}" not found`,
      suggestions: findSimilarElements(targetName, diagram),
    };
  }

  // Find all elements that depend on this one
  const incomingRels = diagram.relationships.filter((rel) => rel.targetId === targetElement.id);
  const dependentElementIds = incomingRels.map((rel) => rel.sourceId);
  const dependentElements = diagram.elements.filter((e) => dependentElementIds.includes(e.id));

  return {
    query,
    elements: [targetElement, ...dependentElements],
    relationships: incomingRels,
    explanation: `Found ${dependentElements.length} element(s) that depend on "${targetElement.name}"`,
    suggestions: [
      `What does ${targetElement.name} depend on?`,
      `Show me the team that owns ${targetElement.name}`,
    ],
  };
}

function findOwnership(
  query: string,
  diagram: ExtendedArchiMateDiagram
): QueryResult {
  // Extract element name from query like "who owns X"
  const match = query.match(/who\s+owns?\s+(.+?)(\?|$)/i);
  if (!match) {
    return {
      query,
      elements: [],
      relationships: [],
      explanation: 'Could not parse ownership query. Try: "Who owns [element name]?"',
    };
  }

  const elementName = match[1].trim();
  const element = diagram.elements.find(
    (e) => e.name.toLowerCase() === elementName.toLowerCase()
  );

  if (!element) {
    return {
      query,
      elements: [],
      relationships: [],
      explanation: `Element "${elementName}" not found`,
    };
  }

  const ownership = diagram.elementMetadata[element.id]?.ownership;

  if (!ownership) {
    return {
      query,
      elements: [element],
      relationships: [],
      explanation: `"${element.name}" has no assigned owner`,
      suggestions: [`Show me all unowned elements`],
    };
  }

  return {
    query,
    elements: [element],
    relationships: [],
    explanation: `"${element.name}" is owned by ${ownership.team} (${ownership.owner})`,
    suggestions: [`Show me all elements owned by ${ownership.team}`],
  };
}

function extractKeywords(query: string): string[] {
  // Remove common words
  const stopWords = new Set([
    'show',
    'me',
    'all',
    'the',
    'that',
    'which',
    'what',
    'where',
    'who',
    'how',
    'with',
    'and',
    'or',
    'in',
    'on',
    'at',
    'to',
    'for',
    'of',
    'from',
  ]);

  return query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 2 && !stopWords.has(word));
}

function findSimilarElements(name: string, diagram: ExtendedArchiMateDiagram): string[] {
  const lowerName = name.toLowerCase();
  const similar = diagram.elements
    .filter((e) => {
      const similarity = calculateSimilarity(lowerName, e.name.toLowerCase());
      return similarity > 0.5;
    })
    .map((e) => `Did you mean "${e.name}"?`)
    .slice(0, 3);

  return similar;
}

function calculateSimilarity(str1: string, str2: string): number {
  // Simple Levenshtein distance-based similarity
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) return 1.0;

  const distance = levenshteinDistance(longer, shorter);
  return (longer.length - distance) / longer.length;
}

function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}

function generateSuggestions(query: string, results: ArchiMateElement[]): string[] {
  const suggestions: string[] = [];

  if (results.length === 1) {
    const element = results[0];
    suggestions.push(`What depends on ${element.name}?`);
    suggestions.push(`Who owns ${element.name}?`);
    suggestions.push(`Show me ${element.layer} layer elements`);
  } else if (results.length > 1) {
    const layers = new Set(results.map((e) => e.layer));
    if (layers.size > 1) {
      suggestions.push(`Show me only ${Array.from(layers)[0]} layer elements`);
    }

    const types = new Set(results.map((e) => e.type));
    if (types.size > 1) {
      suggestions.push(`Show me only ${Array.from(types)[0]} elements`);
    }
  }

  return suggestions.slice(0, 3);
}

/**
 * Pre-built query templates
 */

export const QUERY_TEMPLATES = [
  {
    category: 'Discovery',
    queries: [
      'Show me all services',
      'Show me all application components',
      'Show me all business processes',
      'Show me technology layer elements',
    ],
  },
  {
    category: 'Dependencies',
    queries: [
      'What depends on [element name]?',
      'What does [element name] depend on?',
      'Show me the critical path',
    ],
  },
  {
    category: 'Security',
    queries: [
      'Show me all PII data',
      'Show me public-facing components',
      'Show me unencrypted data flows',
      'Show me high-risk elements',
    ],
  },
  {
    category: 'Ownership',
    queries: [
      'Who owns [element name]?',
      'Show me all unowned elements',
      'Show me elements owned by [team name]',
    ],
  },
  {
    category: 'Technical Debt',
    queries: [
      'Show me high technical debt',
      'Show me legacy components',
      'Show me elements with security debt',
    ],
  },
];

export function suggestQueries(diagram: ExtendedArchiMateDiagram): string[] {
  const suggestions: string[] = [];

  // Suggest based on diagram content
  const layers = new Set(diagram.elements.map((e) => e.layer));
  layers.forEach((layer) => {
    suggestions.push(`Show me all ${layer} layer elements`);
  });

  const teams = new Set<string>();
  diagram.elements.forEach((element) => {
    const ownership = diagram.elementMetadata[element.id]?.ownership;
    if (ownership) teams.add(ownership.team);
  });

  teams.forEach((team) => {
    suggestions.push(`Show me elements owned by ${team}`);
  });

  return suggestions.slice(0, 10);
}

export function exportQueryResults(result: QueryResult): string {
  let output = `# Query Results\n\n`;
  output += `**Query:** ${result.query}\n\n`;
  output += `**Explanation:** ${result.explanation}\n\n`;

  if (result.elements.length > 0) {
    output += `## Elements (${result.elements.length})\n\n`;
    result.elements.forEach((element) => {
      output += `- ${element.name} (${element.type}, ${element.layer})\n`;
    });
  }

  if (result.relationships.length > 0) {
    output += `\n## Relationships (${result.relationships.length})\n\n`;
    result.relationships.forEach((rel) => {
      output += `- ${rel.type}\n`;
    });
  }

  if (result.suggestions && result.suggestions.length > 0) {
    output += `\n## Suggested Queries\n\n`;
    result.suggestions.forEach((suggestion) => {
      output += `- ${suggestion}\n`;
    });
  }

  return output;
}

// Aliases for backward compatibility
export const parseNaturalLanguageQuery = parseQuery;
export const getSuggestedQueries = suggestQueries;
